import React from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import Button from "../Button/Button";
import type { Severity } from "../../design-tokens";

interface ConfirmModalProps {
  /** Controls visibility of the modal */
  visible: boolean;
  /** Callback when user cancels or closes the modal */
  onHide: () => void;
  /** Callback when user confirms the action */
  onConfirm: () => void;
  /** Modal header title (localized by default) */
  title?: string;
  /** Body message (localized by default) */
  message?: string;
  /** Intensity of the action @default "primary" */
  severity?: Severity;
  /** Custom label for confirmation button */
  confirmLabel?: string;
  /** Custom label for cancellation button */
  cancelLabel?: string;
}

/**
 * ConfirmModal Component
 * 
 * Standard confirmation dialog for the system.
 * Replaces PrimeReact's confirmDialog with a Design System-compliant implementation.
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  visible,
  onHide,
  onConfirm,
  title,
  message,
  severity = "primary",
  confirmLabel,
  cancelLabel,
}) => {
  const { t } = useTranslation("form");

  const footer = (
    <div style={{ display: "flex", gap: "1rem", justifyContent: "flex-end", width: "100%" }}>
      <Button
        label={cancelLabel || t("no")}
        variant="outlined"
        severity="neutral"
        onClick={onHide}
      />
      <Button
        label={confirmLabel || t("yes")}
        severity={severity}
        onClick={() => {
          onConfirm();
          onHide();
        }}
      />
    </div>
  );

  return (
    <Modal
      visible={visible}
      onHide={onHide}
      header={title || t("confirmation")}
      size="sm"
      footer={footer}
    >
      <div style={{ 
        fontSize: "1rem", 
        color: "var(--color-neutral-700)",
        lineHeight: "1.5"
      }}>
        {message || t("cancelCreationMessage")}
      </div>
    </Modal>
  );
};

export default ConfirmModal;
